import { useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Tag } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const PartnerDetails = () => {
  const location = useLocation();
  const partner = location.state?.partner;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!partner) return;
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/products?manufacturer=${encodeURIComponent(
            partner.companyName
          )}`
        );
        setProducts(response.data);
      } catch (err) {
        setError("Failed to fetch products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [partner]);

  return (
    <div className="w-full px-8 mt-5">
      {/* Partner Info */}
      <h2 className="text-3xl font-extrabold text-gray-900">
        {partner?.companyName}
      </h2>
      <p className="mt-2 text-gray-600 text-lg">{partner?.country}</p>

      {/* Tags */}
      {partner?.tags?.length > 0 && (
        <div className="flex items-center">
          <div className="lg:flex items-center">
            <h3 className="text-lg text-gray-500 mr-2">Tags:</h3>
            <div className="flex flex-wrap gap-2 mt-2">
              {partner.tags.map((tag, index) => (
                <span
                  key={index}
                  className="text-sm font-medium text-gray-700 border border-gray-400 px-3 py-1 rounded-md"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Company Image */}
      {/* Company Info Section (Image Fixed, Description Scrolls) */}
      <div className="mt-6 flex flex-col md:flex-row gap-6 relative">
        {/* Company Image (Fixed) */}
        <div className="md:w-1/2">
          <div className="bg-gray-100 p-4 rounded-lg flex justify-center sticky top-20">
            <img
              src={partner?.photos?.[0]?.data || "/placeholder.png"}
              alt={partner?.companyName}
              className="w-full max-h-80 object-contain"
            />
          </div>
        </div>

        {/* Company Description (Scrollable) */}
        <div className="hidden lg:block md:w-1/2 max-h-[500px] overflow-y-auto">
          <button
            onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
            className="flex items-center justify-between w-full text-lg font-semibold bg-gray-200 px-4 py-3 rounded-md transition shadow-sm"
          >
            Company Description
            {isDescriptionOpen ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </button>

          {/* Collapsible Section with Background */}
          <div
            className={`overflow-hidden transition-all duration-300 px-4 py-3 ${
              isDescriptionOpen ? "max-h-full opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <p className="text-gray-700 text-md font-sans whitespace-pre-wrap leading-relaxed">
              {partner?.description}
            </p>
          </div>
        </div>
      </div>
      {/* Tags */}
      {/* {partner?.tags?.length > 0 && (
        <div className="">
          <h3 className="text-lg font-semibold text-gray-900">Tags</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {partner.tags.map((tag, index) => (
              <span
                key={index}
                className="text-sm font-medium text-gray-700 border border-gray-400 px-3 py-1 rounded-md"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )} */}

      {/* <button className="bg-gray-200 text-black px-4 py-2 w-1/2 rounded-lg flex justify-center gap-5 shadow-sm my-3">
        <FileImage />
        View Brochure
      </button> */}

      {/* Company Description (Collapsible) */}
      <div className="lg:hidden mt-6 mb-2">
        <button
          onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
          className="flex items-center justify-between w-full text-lg font-semibold bg-gray-200 px-4 py-3 rounded-md transition shadow-sm"
        >
          Company Description
          {isDescriptionOpen ? (
            <ChevronUp size={20} />
          ) : (
            <ChevronDown size={20} />
          )}
        </button>

        {/* Collapsible Section with Background */}
        <div
          className={`overflow-hidden transition-all duration-300 px-4 py-3 ${
            isDescriptionOpen ? "max-h-full opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <pre className=" text-gray-700 text-md font-sans whitespace-pre-wrap">
            {partner?.description}
          </pre>
        </div>
      </div>

      {/* Products Section */}
      <div className="mt-10">
        <h2 className="text-2xl font-bold text-gray-900">
          Products by {partner?.companyName}
        </h2>

        {loading && <p className="mt-4 text-gray-600">Loading products...</p>}
        {error && <p className="mt-4 text-red-500">{error}</p>}

        {!loading && !error && products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6 justify-center place-items-center">
            {products.map((product) => (
              <Link
                key={product._id}
                to={`/product/${product._id}`}
                state={{ product }}
                className="transform transition duration-300 hover:scale-[1.02] h-full"
              >
                <div className="p-4 h-full w-80 border rounded-2xl shadow-md hover:shadow-2xl transition-shadow bg-white flex flex-col gap-3">
                  {/* Product Image */}
                  <div className="w-full h-44 flex items-center justify-center bg-gray-100 rounded-xl overflow-hidden">
                    <img
                      src={
                        product.photos?.[0]?.data ||
                        "https://via.placeholder.com/150"
                      }
                      alt={product.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="">
                    <h2 className="text-lg font-bold text-gray-900 truncate">
                      {product.name}
                    </h2>
                    <h3 className="text-md font-semibold text-gray-700">
                      {product.manufacturer}
                    </h3>

                    <hr className="border-t-1 my-2 border-dashed border-gray-500" />

                    {/* Tags */}
                    {product.tags?.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        <span className="text-sm inline-flex items-center  text-gray-600">
                          <Tag className="w-3 h-3" />
                          Tags:
                        </span>
                        <div className="">
                          {product.tags.map((tag, index) => (
                            <span className="inline-flex items-center text-sm font-medium text-gray-700 border border-gray-400 px-2 py-1 rounded-md my-1 mx-1">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerDetails;
